import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getDailySeed, hashCode } from '../utils/formatting';

interface PlaylistParams {
  genre?: string;
  subGenre?: string;
  search?: string;
  sortBy?: 'default' | 'followers-high' | 'followers-low';
}

interface PlaylistState {
  playlists: any[];
  currentPage: number;
  filters: PlaylistParams;
}

const STORAGE_KEY = 'playlist_state';

export function usePlaylists(params?: PlaylistParams) {
  const [playlists, setPlaylists] = useState<any[]>(() => {
    try {
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState) as PlaylistState;
        if (JSON.stringify(state.filters) === JSON.stringify(params)) {
          return state.playlists;
        }
      }
      return [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState) as PlaylistState;
        if (JSON.stringify(state.filters) === JSON.stringify(params)) {
          return state.currentPage;
        }
      }
      return 1;
    } catch {
      return 1;
    }
  });
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  // Handle prerenderReady flag
  useEffect(() => {
    if (!loading && totalCount > 0) {
      // All critical data has loaded, tell prerender service we're ready
      if (typeof window !== 'undefined' && window.prerenderReady !== undefined) {
        window.prerenderReady = true;
      }
    }
  }, [loading, totalCount]);

  useEffect(() => {
    const filtersChanged = () => {
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState) as PlaylistState;
        return JSON.stringify(state.filters) !== JSON.stringify(params);
      }
      return true;
    };

    if (filtersChanged()) {
      setPlaylists([]);
      setCurrentPage(1);
      setHasMore(true);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [params?.genre, params?.subGenre, params?.search, params?.sortBy]);

  useEffect(() => {
    if (playlists.length > 0) {
      const state: PlaylistState = {
        playlists,
        currentPage,
        filters: params || {}
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [playlists, currentPage, params]);

  useEffect(() => {
    async function fetchPlaylists() {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('playlist_details')
          .select('*', { count: 'exact' });

        if (params?.genre) {
          query = query.eq('genre', params.genre);
        }

        if (params?.subGenre) {
          query = query.contains('sub_genres', [params.subGenre]);
        }

        if (params?.search) {
          const searchTerm = `%${params.search}%`;
          query = query.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
        }

        // Add sorting
        switch (params?.sortBy) {
          case 'followers-high':
          case 'followers-low':
            // Handle featured playlists first (random selection, not sorted)
            if (!params?.genre && !params?.subGenre) {
              // First get featured playlists (randomly selected)
              let featuredQuery = supabase
                .from('playlist_details')
                .select('*')
                .eq('featured', true)
                .order('id', { ascending: false }); // Consistent ordering before shuffling
                
              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                featuredQuery = featuredQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }
              
              const { data: featuredPlaylists } = await featuredQuery;

              // Shuffle featured playlists using daily seed
              const shuffledFeatured = featuredPlaylists ? [...featuredPlaylists].sort((a, b) => {
                const seed = getDailySeed();
                const hashA = hashCode(`${seed}-featured-${a.id}`);
                const hashB = hashCode(`${seed}-featured-${b.id}`);
                return hashA - hashB;
              }).slice(0, 4) : [];

              // Then get ALL playlists sorted by followers
              let playlistsNeeded: number;
              let start: number;
              
              if (currentPage === 1) {
                // First page: we need enough to make 12 total
                playlistsNeeded = Math.max(8, 12 - shuffledFeatured.length);
                start = 0;
              } else {
                // Subsequent pages: always fetch 12 to maintain grid
                playlistsNeeded = 12;
                const regularOnFirstPage = 12 - shuffledFeatured.length;
                start = regularOnFirstPage + ((currentPage - 2) * 12);
              }
              
              let allPlaylistsQuery = supabase
                .from('playlist_details')
                .select('*', { count: 'exact' })
                .order('followers', { ascending: params.sortBy === 'followers-low' })
                .order('id', { ascending: false }); // Secondary sort for stable ordering

              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                allPlaylistsQuery = allPlaylistsQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }

              const { data: allPlaylists, count: totalPlaylistCount } = await allPlaylistsQuery
                .range(start, start + playlistsNeeded - 1);

              if (currentPage === 1) {
                // On first page, combine shuffled featured and sorted playlists
                const combinedPlaylists = [...shuffledFeatured, ...(allPlaylists || [])];
                setPlaylists(combinedPlaylists);
              } else {
                // On subsequent pages, just append all fetched playlists
                setPlaylists(prev => {
                  return [...prev, ...(allPlaylists || [])];
                });
              }

              setTotalCount(totalPlaylistCount || 0);
              setHasMore((start + playlistsNeeded) < (totalPlaylistCount || 0));
              setLoading(false);
              return;
            } else {
              // When genre is selected with follower sorting
              let featuredQuery = supabase
                .from('playlist_details')
                .select('*')
                .eq('featured', true)
                .eq('genre', params.genre)
                .order('id', { ascending: false }); // Consistent ordering before shuffling
                
              if (params?.subGenre) {
                featuredQuery = featuredQuery.contains('sub_genres', [params.subGenre]);
              }
                
              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                featuredQuery = featuredQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }
              
              const { data: featuredPlaylists } = await featuredQuery;

              // Shuffle featured playlists using daily seed
              const shuffledFeatured = featuredPlaylists ? [...featuredPlaylists].sort((a, b) => {
                const seed = getDailySeed();
                const hashA = hashCode(`${seed}-featured-${a.id}`);
                const hashB = hashCode(`${seed}-featured-${b.id}`);
                return hashA - hashB;
              }).slice(0, 4) : [];

              // Get ALL playlists for the selected genre sorted by followers
              let playlistsNeeded: number;
              let start: number;
              
              if (currentPage === 1) {
                // First page: we need enough to make 12 total
                playlistsNeeded = Math.max(8, 12 - shuffledFeatured.length);
                start = 0;
              } else {
                // Subsequent pages: always fetch 12 to maintain grid
                playlistsNeeded = 12;
                const regularOnFirstPage = 12 - shuffledFeatured.length;
                start = regularOnFirstPage + ((currentPage - 2) * 12);
              }
              
              let allPlaylistsQuery = supabase
                .from('playlist_details')
                .select('*', { count: 'exact' })
                .eq('genre', params.genre)
                .order('followers', { ascending: params.sortBy === 'followers-low' })
                .order('id', { ascending: false }); // Secondary sort for stable ordering
                
              if (params?.subGenre) {
                allPlaylistsQuery = allPlaylistsQuery.contains('sub_genres', [params.subGenre]);
              }

              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                allPlaylistsQuery = allPlaylistsQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }

              const { data: allPlaylists, count: totalPlaylistCount } = await allPlaylistsQuery
                .range(start, start + playlistsNeeded - 1);

              if (currentPage === 1) {
                // On first page, combine shuffled featured and sorted playlists
                const combinedPlaylists = [...shuffledFeatured, ...(allPlaylists || [])];
                setPlaylists(combinedPlaylists);
              } else {
                // On subsequent pages, just append all fetched playlists
                setPlaylists(prev => {
                  return [...prev, ...(allPlaylists || [])];
                });
              }

              setTotalCount(totalPlaylistCount || 0);
              setHasMore((start + playlistsNeeded) < (totalPlaylistCount || 0));
              setLoading(false);
              return;
            }
          case 'default':
          default:
            // If no genre is selected, limit featured playlists to 4
            if (!params?.genre && !params?.subGenre) {
              // First get featured playlists (randomly selected, not by date)
              let featuredQuery = supabase
                .from('playlist_details')
                .select('*')
                .eq('featured', true)
                .order('id', { ascending: false }); // Consistent ordering before shuffling
                
              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                featuredQuery = featuredQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }
              
              const { data: featuredPlaylists } = await featuredQuery;

              // Shuffle featured playlists using daily seed
              const shuffledFeatured = featuredPlaylists ? [...featuredPlaylists].sort((a, b) => {
                const seed = getDailySeed();
                const hashA = hashCode(`${seed}-featured-${a.id}`);
                const hashB = hashCode(`${seed}-featured-${b.id}`);
                return hashA - hashB;
              }).slice(0, 4) : [];

              // Then get ALL playlists (including featured) in chronological order
              // For better grid layout, always fetch in multiples of 4
              let playlistsNeeded: number;
              let start: number;
              
              if (currentPage === 1) {
                // First page: we need enough to make 12 total
                // If we have fewer than 4 featured, we need more regular playlists
                playlistsNeeded = Math.max(8, 12 - shuffledFeatured.length);
                start = 0;
              } else {
                // Subsequent pages: always fetch 12 to maintain grid
                playlistsNeeded = 12;
                // Calculate how many regular playlists were shown on page 1
                const regularOnFirstPage = 12 - shuffledFeatured.length;
                // Start from where we left off
                start = regularOnFirstPage + ((currentPage - 2) * 12);
              }
              
              let allPlaylistsQuery = supabase
                .from('playlist_details')
                .select('*', { count: 'exact' })
                .order('published_at', { ascending: false, nullsFirst: false })
                .order('id', { ascending: false }); // Secondary sort for stable ordering

              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                allPlaylistsQuery = allPlaylistsQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }

              const { data: allPlaylists, count: totalPlaylistCount } = await allPlaylistsQuery
                .range(start, start + playlistsNeeded - 1);

              if (currentPage === 1) {
                // On first page, combine shuffled featured and non-featured
                const combinedPlaylists = [...shuffledFeatured, ...(allPlaylists || [])];
                setPlaylists(combinedPlaylists);
              } else {
                // On subsequent pages, just append all fetched playlists
                // Don't filter duplicates - featured playlists can appear twice
                setPlaylists(prev => {
                  return [...prev, ...(allPlaylists || [])];
                });
              }

              // Total count is just the total number of playlists (featured are included in the count)
              setTotalCount(totalPlaylistCount || 0);
              setHasMore((start + playlistsNeeded) < (totalPlaylistCount || 0));
              setLoading(false);
              return;
            } else {
              // When genre is selected, still limit featured playlists to 4 (randomly selected)
              let featuredQuery = supabase
                .from('playlist_details')
                .select('*')
                .eq('featured', true)
                .eq('genre', params.genre)
                .order('id', { ascending: false }); // Consistent ordering before shuffling
                
              if (params?.subGenre) {
                featuredQuery = featuredQuery.contains('sub_genres', [params.subGenre]);
              }
                
              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                featuredQuery = featuredQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }
              
              const { data: featuredPlaylists } = await featuredQuery;

              // Shuffle featured playlists using daily seed
              const shuffledFeatured = featuredPlaylists ? [...featuredPlaylists].sort((a, b) => {
                const seed = getDailySeed();
                const hashA = hashCode(`${seed}-featured-${a.id}`);
                const hashB = hashCode(`${seed}-featured-${b.id}`);
                return hashA - hashB;
              }).slice(0, 4) : [];

              // Get ALL playlists for the selected genre (including featured)
              // For better grid layout, always fetch in multiples of 4
              let playlistsNeeded: number;
              let start: number;
              
              if (currentPage === 1) {
                // First page: we need enough to make 12 total
                // If we have fewer than 4 featured, we need more regular playlists
                playlistsNeeded = Math.max(8, 12 - shuffledFeatured.length);
                start = 0;
              } else {
                // Subsequent pages: always fetch 12 to maintain grid
                playlistsNeeded = 12;
                // Calculate how many regular playlists were shown on page 1
                const regularOnFirstPage = 12 - shuffledFeatured.length;
                // Start from where we left off
                start = regularOnFirstPage + ((currentPage - 2) * 12);
              }
              
              let allPlaylistsQuery = supabase
                .from('playlist_details')
                .select('*', { count: 'exact' })
                .eq('genre', params.genre)
                .order('published_at', { ascending: false, nullsFirst: false })
                .order('id', { ascending: false }); // Secondary sort for stable ordering
                
              if (params?.subGenre) {
                allPlaylistsQuery = allPlaylistsQuery.contains('sub_genres', [params.subGenre]);
              }

              if (params?.search) {
                const searchTerm = `%${params.search}%`;
                allPlaylistsQuery = allPlaylistsQuery.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
              }

              const { data: allPlaylists, count: totalPlaylistCount } = await allPlaylistsQuery
                .range(start, start + playlistsNeeded - 1);

              if (currentPage === 1) {
                // On first page, combine shuffled featured and non-featured
                const combinedPlaylists = [...shuffledFeatured, ...(allPlaylists || [])];
                setPlaylists(combinedPlaylists);
              } else {
                // On subsequent pages, just append all fetched playlists
                // Don't filter duplicates - featured playlists can appear twice
                setPlaylists(prev => {
                  return [...prev, ...(allPlaylists || [])];
                });
              }

              // Total count is just the total number of playlists (featured are included in the count)
              setTotalCount(totalPlaylistCount || 0);
              setHasMore((start + playlistsNeeded) < (totalPlaylistCount || 0));
              setLoading(false);
              return;
            }
        }
      } catch (err) {
        console.error('Failed to fetch playlists:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch playlists'));
      } finally {
        setLoading(false);
      }
    }

    fetchPlaylists();
  }, [currentPage, params?.genre, params?.subGenre, params?.search, params?.sortBy]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return {
    playlists,
    loading,
    error,
    hasMore,
    loadMore,
    currentPage,
    totalCount,
    itemsPerPage
  };
}